/*
Purpose: Universal sidebar component with consistent styling across all pages.
Audience: All users.
What it does:
- Provides a unified sidebar interface that adapts content based on context
- Uses consistent casefile-style rounded corners and animations
Interplay:
- Replaces DashboardNav, CasefileDetailNav, and general Sidebar components
*/
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './UniversalSidebar.css';

const UniversalSidebar = ({ 
  isOpen, 
  onToggle, 
  items = [],
  activeItem = null,
  className = '',
  title = null,
  subtitle = null,
  footer = null,
  sidebarMode = 'desktop-manual', // 'mobile', 'desktop-manual', 'auto-expanded'
  shouldAutoExpand = false
}) => {
  const [isMobile, setIsMobile] = useState(false);

  // Detect if we're on mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const sidebarClass = `universal-sidebar ${isOpen ? 'universal-sidebar-open' : ''} ${sidebarMode ? `universal-sidebar-${sidebarMode}` : ''} ${className}`;

  return (
    <>
      {/* Overlay for mobile - tap outside to close */}
      {isOpen && isMobile && (
        <div 
          className="universal-sidebar-overlay" 
          onClick={() => onToggle(false)}
          aria-label="Close sidebar"
        />
      )}
      
      <nav className={sidebarClass}>
        {/* Optional header section */}
        {(title || subtitle) && (
          <div className="nav-header">
            {title && <h3>{title}</h3>}
            {subtitle && <p>{subtitle}</p>}
          </div>
        )}
        
        <div className="nav-items">
          {items.map((item) => {
            if (!item.show && item.show !== undefined) return null;
            
            // Handle dividers
            if (item.isDivider) {
              return <hr key={item.id} className="sidebar-divider" />;
            }
            
            const IconComponent = item.icon;
            const isActive = activeItem === item.id || (item.isToggle && item.isActive);
            
            // Handle link items
            if (item.isLink && item.to) {
              return (
                <Link
                  key={item.id}
                  to={item.to}
                  onClick={() => {
                    if (item.onClick) {
                      item.onClick();
                    }
                    // Close sidebar on mobile after navigation, but not in auto-expanded mode
                    if (isMobile && !shouldAutoExpand) {
                      onToggle(false);
                    }
                  }}
                  className={`nav-item ${isActive ? 'active' : ''}`}
                  title={item.description || item.label}
                  aria-label={item.description || item.label}
                  data-id={item.id}
                  style={item.color ? { '--icon-color': item.color } : {}}
                >
                  <IconComponent className="nav-icon" />
                  <span className="nav-label">{item.label}</span>
                  {/* Support for activity dots or other indicators */}
                  {item.indicator && item.indicator}
                </Link>
              );
            }
            
            // Handle button items
            return (
              <button
                key={item.id}
                onClick={(e) => {
                  if (item.onClick) {
                    // For toggle items, create a synthetic event with target.checked
                    if (item.isToggle) {
                      const syntheticEvent = {
                        target: {
                          checked: !item.isActive
                        }
                      };
                      item.onClick(syntheticEvent);
                    } else {
                      item.onClick(e);
                    }
                  }
                  // Close sidebar on mobile after action, but not in auto-expanded mode
                  if (isMobile && !shouldAutoExpand) {
                    onToggle(false);
                  }
                }}
                className={`nav-item ${isActive ? 'active' : ''}`}
                title={item.description || item.label}
                aria-label={item.description || item.label}
                data-id={item.id}
                style={item.color ? { '--icon-color': item.color } : {}}
              >
                <IconComponent className="nav-icon" />
                <span className="nav-label">{item.label}</span>
                {/* Support for activity dots or other indicators */}
                {item.indicator && item.indicator}
              </button>
            );
          })}
        </div>
        
        {/* Optional footer section */}
        {footer && (
          <div className="nav-footer">
            {footer}
          </div>
        )}
        
        {/* Support for modals or additional content */}
        {items.some(item => item.modal) && (
          <>
            {items.map(item => item.modal && item.modal)}
          </>
        )}
      </nav>
    </>
  );
};

export default UniversalSidebar;
